import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '../../environments/environment';
import { USER_SESSION_STORAGE_KEY } from './user.web.service';

@Injectable({
  providedIn: 'root'
})
export class KeycloakService {
  private keycloak: Keycloak | undefined;
  private initialized = false;
  private refreshTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {}

  /**
   * Initializes Keycloak with the environment configuration.
   * Restores tokens from storage if available and sets up automatic token refresh.
   *
   * @returns A promise that resolves to true if the user is authenticated, false otherwise.
   */
  async init(): Promise<boolean> {
    if (this.initialized) {
      return true;
    }

    try {
      this.keycloak = new Keycloak({
        url: this.extractAuthUrl(environment.loginUrl),
        realm: environment.realm,
        clientId: environment.clientId,
      });

      const storedTokens = this.loadTokensFromStorage();

      const authenticated = await this.keycloak.init({
        onLoad: 'check-sso',
        checkLoginIframe: false,
        pkceMethod: 'S256',
        token: storedTokens.token,
        refreshToken: storedTokens.refreshToken,
        idToken: storedTokens.idToken,
      });

      this.initialized = true;

      // Persist tokens and schedule proactive refresh 30 seconds before expiry
      if (authenticated && this.keycloak.token) {
        this.saveTokensToStorage();
        this.setupTokenRefresh();
      }

      return authenticated;
    } catch (error) {
      this.initialized = false;
      return false;
    }
  }

  /**
   * Extracts the base auth URL from a full Keycloak login URL.
   * Example: https://authentication.dev.docestate.com/realms/DocEstateDev/protocol/openid-connect/auth
   *       -> https://authentication.dev.docestate.com
   *
   * @param loginUrl - The full login URL from the environment configuration.
   * @returns The base URL containing only the protocol and host.
   */
  private extractAuthUrl(loginUrl: string): string {
    const url = new URL(loginUrl);
    return `${url.protocol}//${url.host}`;
  }

  /**
   * Sets up automatic token refresh 30 seconds before expiry.
   * After each successful refresh the next refresh is rescheduled based on the new token's expiry.
   */
  private setupTokenRefresh(): void {
    if (!this.keycloak) return;

    const scheduleRefresh = () => {
      clearTimeout(this.refreshTimer);

      const exp = this.keycloak?.tokenParsed?.['exp'] as number | undefined;
      if (!exp) return;

      const msUntilExpiry = exp * 1000 - Date.now();
      const refreshIn = msUntilExpiry - 30_000; // trigger refresh 30 seconds before expiry

      if (refreshIn <= 0) {
        // Token expires in less than 30 seconds – refresh immediately
        performRefresh();
        return;
      }

      this.refreshTimer = setTimeout(performRefresh, refreshIn);
    };

    const performRefresh = () => {
      // Force refresh regardless of remaining validity (-1 bypasses the min-validity check)
      this.keycloak?.updateToken(-1)
        .then((refreshed) => {
          if (refreshed) {
            this.saveTokensToStorage();
          }
          scheduleRefresh(); // reschedule for the new token
        })
        .catch(() => {
          this.logout();
        });
    };

    scheduleRefresh();
  }

  /**
   * Returns whether the user is currently authenticated.
   *
   * @returns True if the user is logged in, false otherwise.
   */
  isLoggedIn(): boolean {
    return !!this.keycloak?.authenticated;
  }

  /**
   * Redirects the user to the Keycloak login page.
   * After a successful login the user is redirected back to the requested route, or the
   * current page when no target is provided.
   *
   * @param redirectPath - Absolute in-app path to return to after login (e.g. "/dashboard/orders/DE-12345").
   */
  login(redirectPath?: string): void {
    const target = redirectPath ?? window.location.pathname;
    this.keycloak?.login({
      redirectUri: new URL(target, window.location.origin).toString(),
    });
  }

  /**
   * Signs the user out and returns them to the given route so a different account can be used.
   *
   * The logout sends the browser back to `redirectPath`, where the missing session makes
   * {@link authGuard} start the login. The pending token refresh is cancelled so it cannot
   * trigger a second, competing redirect while the logout is in flight.
   *
   * @param redirectPath - Absolute in-app path to return to (e.g. "/dashboard/orders/DE-12345").
   *                       Must be a route behind {@link authGuard}, otherwise no login is triggered.
   */
  loginWithDifferentAccount(redirectPath?: string): void {
    clearTimeout(this.refreshTimer);
    this.clearTokensFromStorage();
    sessionStorage.removeItem(USER_SESSION_STORAGE_KEY);

    const target = redirectPath ?? window.location.pathname;
    this.keycloak?.logout({
      redirectUri: new URL(target, window.location.origin).toString(),
    });
  }

  /**
   * Clears local tokens and redirects the user to the Keycloak logout endpoint.
   * After logout the user is redirected to the application root.
   */
  logout(): void {
    this.clearTokensFromStorage();
    sessionStorage.removeItem(USER_SESSION_STORAGE_KEY);
    this.keycloak?.logout({
      redirectUri: window.location.origin,
    });
  }

  /**
   * Returns the current access token.
   *
   * @returns The JWT access token string, or undefined if not authenticated.
   */
  getToken(): string | undefined {
    return this.keycloak?.token;
  }

  /**
   * Returns the username of the currently authenticated user.
   *
   * @returns The preferred username from the token claims, or undefined if not authenticated.
   */
  getUsername(): string | undefined {
    return this.keycloak?.tokenParsed?.['preferred_username'];
  }

  /**
   * Returns the email address of the currently authenticated user.
   *
   * Falls back to the username, which is the email address for most realm users.
   *
   * @returns The email claim from the token, or undefined if not authenticated.
   */
  getEmail(): string | undefined {
    const email = this.keycloak?.tokenParsed?.['email'] as string | undefined;

    return email?.trim() || this.getUsername();
  }

  /**
   * Returns all realm roles assigned to the currently authenticated user.
   *
   * @returns An array of role name strings, or an empty array if none are present.
   */
  getUserRoles(): string[] {
    const realmAccess = this.keycloak?.tokenParsed?.['realm_access'] as { roles?: string[] } | undefined;
    return realmAccess?.roles || [];
  }

  /**
   * Persists the current access token, refresh token and ID token to localStorage.
   */
  private saveTokensToStorage(): void {
    if (!this.keycloak?.token || !this.keycloak?.refreshToken) return;

    localStorage.setItem('kc_token', this.keycloak.token);
    localStorage.setItem('kc_refresh_token', this.keycloak.refreshToken);
    if (this.keycloak.idToken) {
      localStorage.setItem('kc_id_token', this.keycloak.idToken);
    }
  }

  /**
   * Loads previously persisted tokens from localStorage.
   *
   * @returns An object containing the token, refresh token and ID token, each optional.
   */
  private loadTokensFromStorage(): { token?: string; refreshToken?: string; idToken?: string } {
    return {
      token: localStorage.getItem('kc_token') || undefined,
      refreshToken: localStorage.getItem('kc_refresh_token') || undefined,
      idToken: localStorage.getItem('kc_id_token') || undefined,
    };
  }

  /**
   * Removes all persisted Keycloak tokens from localStorage.
   */
  clearTokensFromStorage(): void {
    localStorage.removeItem('kc_token');
    localStorage.removeItem('kc_refresh_token');
    localStorage.removeItem('kc_id_token');
  }
}
